import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ArticlePreview {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
  image?: string;
  imageAlt?: string;
  readingTime: number;
}

interface LatestArticles {
  en: ArticlePreview[];
  fr: ArticlePreview[];
  ar: ArticlePreview[];
  de?: ArticlePreview[];
}

function useIntersectionObserver(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isInView };
}

function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  const localeMap: Record<string, string> = {
    en: 'en-US',
    fr: 'fr-FR',
    ar: 'ar-MA',
    de: 'de-DE',
  };
  return date.toLocaleDateString(localeMap[locale] || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function ArticleCard({
  article,
  locale,
  index,
  isInView,
}: {
  article: ArticlePreview;
  locale: string;
  index: number;
  isInView: boolean;
}) {
  const { t } = useTranslation();
  const blogUrl = `/blog/${locale}/${article.slug}/`;

  // Blog images are served from /blog/images/ but MDX has paths like /images/...
  // So we need to prepend /blog to the image path
  const getImagePath = (path: string) => `/blog${path}`;

  return (
    <div
      className={cn(
        'transition-all duration-700',
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <Card className="group h-full overflow-hidden border-2 border-border hover:border-primary/50 transition-colors">
        {article.image && (
          <a href={blogUrl} className="block overflow-hidden">
            <div className="aspect-video overflow-hidden bg-muted">
              <img
                src={getImagePath(article.image)}
                srcSet={`${getImagePath(article.image.replace('.webp', '-400w.webp'))} 400w, ${getImagePath(article.image.replace('.webp', '-800w.webp'))} 800w`}
                sizes="(max-width: 640px) 100vw, 50vw"
                alt={article.imageAlt || article.title}
                width={800}
                height={450}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            </div>
          </a>
        )}
        <CardHeader className="p-4 sm:p-6">
          <div className="mb-2 flex flex-wrap gap-2">
            {article.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] sm:text-xs px-2 py-0.5">
                {tag}
              </Badge>
            ))}
          </div>
          <CardTitle className="text-base sm:text-lg line-clamp-2">
            <a href={blogUrl} className="transition-colors hover:text-primary">
              {article.title}
            </a>
          </CardTitle>
          <CardDescription className="mt-2 line-clamp-2 text-sm">
            {article.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
            <span>{formatDate(article.date, locale)}</span>
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {article.readingTime} {t('blog-min-read') || 'min'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function BlogPreviewSection() {
  const { t, i18n } = useTranslation();
  const { ref, isInView } = useIntersectionObserver();
  const [articles, setArticles] = useState<ArticlePreview[]>([]);
  const [loading, setLoading] = useState(true);

  const locale = (i18n.language || 'en') as keyof LatestArticles;

  useEffect(() => {
    // Fetch latest articles from the blog
    fetch('/blog/latest-articles.json')
      .then((res) => res.json())
      .then((data: LatestArticles) => {
        const localeArticles = data[locale]?.length ? data[locale] : data.en || [];
        setArticles(localeArticles);
        setLoading(false);
      })
      .catch(() => {
        // Fallback to empty if fetch fails
        setLoading(false);
      });
  }, [locale]);

  const blogIndexUrl = `/blog/${locale}/`;

  // Don't render if no articles
  if (!loading && articles.length === 0) {
    return null;
  }

  return (
    <section ref={ref} className="landing-section py-16 sm:py-20 md:py-28 border-t-2 border-border">
      <div className="mx-auto max-w-6xl px-4">
        <div
          className={cn(
            'mb-8 sm:mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 transition-all duration-1000',
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <div>
            <Badge
              variant="secondary"
              className="mb-3 sm:mb-4 border-2 border-foreground text-xs sm:text-sm"
            >
              <BookOpen className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {t('landing-blog-eyebrow') || 'From our blog'}
            </Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
              {t('landing-blog-title') || 'Latest insights'}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl">
              {t('landing-blog-subtitle') ||
                'Tips, guides, and updates on solar PV engineering and AI-powered design.'}
            </p>
          </div>
          <Button variant="outline" size="sm" className="self-start sm:self-auto" asChild>
            <a href={blogIndexUrl}>
              {t('landing-blog-view-all') || 'View all articles'}
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2">
            {[0, 1].map((i) => (
              <Card key={i} className="h-full border-2 border-border">
                <div className="aspect-video animate-pulse bg-muted" />
                <CardHeader className="p-4 sm:p-6">
                  <div className="mb-2 flex gap-2">
                    <div className="h-5 w-16 animate-pulse rounded-full bg-muted" />
                    <div className="h-5 w-20 animate-pulse rounded-full bg-muted" />
                  </div>
                  <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
                  <div className="mt-2 space-y-2">
                    <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {articles.map((article, index) => (
              <ArticleCard
                key={article.slug}
                article={article}
                locale={locale}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
